import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import FileUploader from '../file_uploader/FileUploader.jsx';
import API_ENDPOINTS from '../ApiEndPoints';
import AttachmentCardView from './AttachmentCardView.jsx';
import AttachmentListView from './AttachmentListView.jsx';
import AttachmentEditor from './AttachmentEditor.jsx';
import AttachmentPreviewModal from './AttachmentPreviewModal.jsx';
import { classNames } from './helpers';

const VIEW_STORAGE_KEY = 'instance_attachment_manager_view';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const AttachmentManager = ({ instanceId, templateId, instanceTitle, defaultView = 'cards', uploadApiBase }) => {
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultView;
    }
    return localStorage.getItem(VIEW_STORAGE_KEY) || defaultView;
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [visibility, setVisibility] = useState('all');
  const [sort, setSort] = useState('created_at');
  const [direction, setDirection] = useState('desc');
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0, last_page: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const endpoint = useMemo(() => API_ENDPOINTS.attachments.listForInstance(instanceId), [instanceId]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    }
  }, [viewMode]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        search: debouncedSearch || undefined,
        visibility,
        sort,
        direction,
      };
      const { data } = await axios.get(endpoint, { params });
      setRows(data.data || []);
  setPagination((prev) => (data.pagination ? data.pagination : prev));
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to load attachments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, debouncedSearch, visibility, sort, direction, page, perPage]);

  const refreshAfterUpload = () => {
    setPage(1);
    load();
  };

  const openEditor = (attachment) => {
    setSelected(attachment);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setSelected(null);
    setEditorOpen(false);
  };

  const handleSave = async (payload) => {
    if (!selected) return;
    setSaving(true);
    try {
      const url = API_ENDPOINTS.attachments.update(instanceId, selected.id);
      await axios.put(url, payload);
      closeEditor();
      load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to save attachment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (attachment) => {
    try {
      const url = API_ENDPOINTS.attachments.delete(instanceId, attachment.id);
      await axios.delete(url);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to delete attachment');
    }
  };

  const openPreview = (attachment) => {
    if (!attachment) return;
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewAttachment(null);
    setPreviewOpen(false);
  };

  const totalPages = Math.max(1, pagination?.last_page || 1);

  const headerSubtitle = instanceTitle
    ? `Managing attachments for ${instanceTitle}`
    : `Instance #${instanceId}`;

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="m-0">Attachments</h4>
            <div className="text-xs text-gray-500">{headerSubtitle}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="btn-group btn-group-xs">
              <button
                type="button"
                className={classNames('btn btn-default', viewMode === 'cards' && 'active')}
                onClick={() => setViewMode('cards')}
              >
                Card view
              </button>
              <button
                type="button"
                className={classNames('btn btn-default', viewMode === 'list' && 'active')}
                onClick={() => setViewMode('list')}
              >
                List view
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-4 panel-body">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 p-3 border border-gray-200 border-dashed rounded bg-gray-50 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-800">Upload attachments</div>
              <div className="text-xs text-gray-500">Queue multiple files, then click Upload when you're ready.</div>
            </div>
            <div className="flex justify-start w-full md:w-auto md:justify-end">
              <FileUploader
                templateId={templateId}
                instanceId={instanceId}
                uploadApiBase={uploadApiBase}
                onComplete={refreshAfterUpload}
              />
            </div>
          </div>
          <div className="grid gap-2 md:grid-cols-4 lg:grid-cols-5">
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block mb-1 text-xs font-semibold text-gray-600">Search</label>
              <input
                type="text"
                className="w-full form-control input-sm"
                placeholder="Title, description or tags"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-gray-600">Visibility</label>
              <select className="w-full form-control input-sm" value={visibility} onChange={(e) => { setVisibility(e.target.value); setPage(1); }}>
                <option value="all">All</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-gray-600">Sort</label>
              <select className="w-full form-control input-sm" value={`${sort}:${direction}`} onChange={(e) => {
                const [nextSort, nextDirection] = e.target.value.split(':');
                setSort(nextSort);
                setDirection(nextDirection);
                setPage(1);
              }}>
                <option value="created_at:desc">Newest</option>
                <option value="created_at:asc">Oldest</option>
                <option value="title:asc">Title A-Z</option>
                <option value="title:desc">Title Z-A</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-gray-600">Page size</label>
              <select className="w-full form-control input-sm" value={perPage} onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}>
                {[10, 20, 30, 50].map((size) => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {loading ? (
          <div className="py-5 text-center text-muted">Loading attachments…</div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <AttachmentCardView items={rows} onEdit={openEditor} onDelete={handleDelete} onPreview={openPreview} />
            ) : (
              <AttachmentListView items={rows} onEdit={openEditor} onDelete={handleDelete} onPreview={openPreview} />
            )}
            <div className="flex flex-col gap-3 pt-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-gray-500">
                Page {pagination.page || 1} of {totalPages} • {pagination.total || rows.length} attachment(s)
              </div>
              <div className="btn-group btn-group-xs">
                <button type="button" className="btn btn-default" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Previous</button>
                <button type="button" className="btn btn-default" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      <AttachmentEditor
        attachment={selected}
        open={editorOpen}
        onClose={closeEditor}
        onSave={handleSave}
        busy={saving}
      />
      <AttachmentPreviewModal attachment={previewAttachment} open={previewOpen} onClose={closePreview} />
    </div>
  );
};

AttachmentManager.propTypes = {
  instanceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  instanceTitle: PropTypes.string,
  defaultView: PropTypes.oneOf(['cards', 'list']),
  uploadApiBase: PropTypes.string,
};

AttachmentManager.defaultProps = {
  instanceTitle: null,
  defaultView: 'cards',
  uploadApiBase: null,
};

export default AttachmentManager;
